/**
 * Shared custom element registration with property-upgrade support.
 *
 * Custom elements created inside <template> content (e.g. by lit-html) are
 * inert until connected to the DOM.  If a framework sets properties on them
 * before upgrade, the assignment creates instance data-properties that
 * permanently shadow prototype getters/setters.
 *
 * This wrapper detects accessor properties on the class prototype and wraps
 * connectedCallback so that any shadowing instance properties are deleted
 * and re-assigned through the real setter at upgrade time.
 */
export function register(className: string, classType: any) {
    if (customElements.get(className)) return

    const proto = classType.prototype
    const accessorProps: string[] = []
    for (const key of Object.getOwnPropertyNames(proto)) {
        const desc = Object.getOwnPropertyDescriptor(proto, key)
        if (desc && desc.get && desc.set) accessorProps.push(key)
    }

    if (accessorProps.length > 0) {
        const originalConnected = proto.connectedCallback
        proto.connectedCallback = function (this: HTMLElement) {
            for (const prop of accessorProps) {
                if (this.hasOwnProperty(prop)) {
                    const value = (this as any)[prop]
                    delete (this as any)[prop]
                    ;(this as any)[prop] = value
                }
            }
            if (originalConnected) originalConnected.call(this)
        }
    }

    customElements.define(className, classType)
}
